/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Avatar, Button, Tag, Typography } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  MessageOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { CommentForm } from './CommentForm';
import type { ReviewComment, CommentFormData } from './types';
import { getInitials } from './utils';

type CommentItemProps = {
  comment: ReviewComment;
  isCurrentUser?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onReply?: () => void;
  level?: number;
  // Handler factories for recursive answer handling
  onEditAny?: (commentId: number) => void;
  onDeleteAny?: (commentId: number) => void;
  editingCommentId?: number | null;
  onCancelEdit?: () => void;
  onSubmitEdit?: (commentId: number, data: CommentFormData) => void;
  isSubmittingEdit?: boolean;
  isReadOnly?: boolean;
};

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isCurrentUser = false,
  onEdit,
  onDelete,
  onReply,
  level = 0,
  // New props for recursive handling
  onEditAny,
  onDeleteAny,
  editingCommentId,
  onCancelEdit,
  onSubmitEdit,
  isSubmittingEdit = false,
  isReadOnly = false,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const summaryTags = comment.summary
    ? comment.summary
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];

  const isAnswer = level > 0;
  const isEditing = editingCommentId === comment.id;

  return (
    <div css={[styles.commentItem, isAnswer && styles.commentItemAnswer]}>
      {isEditing && onSubmitEdit && onCancelEdit ? (
        <CommentForm
          initialData={{
            title: comment.title || undefined,
            summary: comment.summary || undefined,
            description: comment.description || undefined,
          }}
          onSubmit={(data) => onSubmitEdit(comment.id, data)}
          onCancel={onCancelEdit}
          isSubmitting={isSubmittingEdit}
        />
      ) : (
        <React.Fragment>
          <div css={styles.header}>
            <div css={styles.authorSection}>
              <Avatar css={styles.avatar} icon={<UserOutlined rev={''} />}>
                {getInitials(comment.created_user_full_name)}
              </Avatar>

              <div css={styles.authorInfo}>
                <Typography.Text strong css={styles.authorName}>
                  {comment.created_user_full_name ||
                    intl.formatMessage({ id: 'review-unknown-user' })}
                </Typography.Text>
                <Typography.Text type="secondary" css={styles.timestamp}>
                  {moment.unix(comment.created_at).fromNow()}
                  {comment.is_edited && (
                    <span css={styles.editedLabel}>
                      {' '}
                      • {intl.formatMessage({ id: 'comment-edited-label' })}
                    </span>
                  )}
                </Typography.Text>
              </div>
            </div>

            <div css={styles.actions}>
              {onEdit && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined rev={''} />}
                  onClick={onEdit}
                  css={styles.actionButton}>
                  {intl.formatMessage({ id: 'comment-edit-button' })}
                </Button>
              )}
              {onDelete && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined rev={''} />}
                  onClick={onDelete}
                  css={styles.actionButton}>
                  {intl.formatMessage({ id: 'comment-delete-button' })}
                </Button>
              )}
              {!isAnswer && onReply && (
                <Button
                  type="text"
                  size="small"
                  icon={<MessageOutlined rev={''} />}
                  onClick={onReply}
                  css={styles.actionButton}>
                  {intl.formatMessage({ id: 'comment-reply-button' })}
                </Button>
              )}
            </div>
          </div>

          {comment.title && (
            <Typography.Text strong css={styles.title}>
              {comment.title}
            </Typography.Text>
          )}

          {summaryTags.length > 0 && (
            <div css={styles.summaryTags}>
              {summaryTags.map((tag, index) => (
                <Tag key={index} color="blue" css={styles.summaryTag}>
                  {tag}
                </Tag>
              ))}
            </div>
          )}

          {comment.description && (
            <Typography.Paragraph css={styles.description}>
              {comment.description}
            </Typography.Paragraph>
          )}

          {comment.answers && comment.answers.length > 0 && (
            <div css={styles.answersContainer}>
              {comment.answers.map((answer) => (
                <CommentItem
                  key={answer.id}
                  comment={answer}
                  isCurrentUser={false}
                  level={level + 1}
                  onEdit={
                    answer.can_edit && onEditAny
                      ? () => onEditAny(answer.id)
                      : undefined
                  }
                  onDelete={
                    answer.can_delete && onDeleteAny
                      ? () => onDeleteAny(answer.id)
                      : undefined
                  }
                  onEditAny={onEditAny}
                  onDeleteAny={onDeleteAny}
                  editingCommentId={editingCommentId}
                  onCancelEdit={onCancelEdit}
                  onSubmitEdit={onSubmitEdit}
                  isSubmittingEdit={isSubmittingEdit}
                  isReadOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  commentItem: {
    background: '#ffffff',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 12,
    transition: 'box-shadow 0.2s ease',

    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
  },

  commentItemAnswer: {
    background: '#fafafa',
    marginLeft: 24,
    borderLeft: '3px solid #1890ff',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
  },

  authorSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flex: 1,
  },

  avatar: {
    background: '#e6f7ff',
    color: '#1890ff',
    flexShrink: 0,
    width: 32,
    height: 32,
    lineHeight: '32px',
    fontSize: 14,
  },

  authorInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },

  authorName: {
    fontSize: 13,
  },

  timestamp: {
    fontSize: 11,
  },

  editedLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    fontStyle: 'italic',
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },

  actionButton: {
    fontSize: 11,
    padding: '0 4px',
  },

  title: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 6,
  },

  summaryTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },

  summaryTag: {
    margin: 0,
    fontSize: 11,
  },

  description: {
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#595959',
  },

  answersContainer: {
    marginTop: 12,
  },
});
